import { useState, useEffect, forwardRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AccountPermissionTab } from "@/features/permission";
import { useManagerDetail, useUpdateManager, useInsertManager } from "../hooks/useManagers";

// --- Schemas ---

const editSchema = z.object({
  name: z.string().optional(),
  password: z.string().optional(),
  phoneNum: z.string().optional(),
  email: z.string().optional(),
  department: z.string().optional(),
  position: z.string().optional(),
  memo: z.string().optional(),
  smsPhoneNum: z.string().optional(),
  callPassContactNum: z.string().optional(),
});

const addSchema = z.object({
  id: z.string().min(1, "아이디는 필수입니다"),
  password: z.string().min(1, "비밀번호는 필수입니다"),
  name: z.string().min(1, "이름은 필수입니다"),
  phoneNum: z.string().optional(),
  email: z.string().optional(),
  department: z.string().optional(),
  position: z.string().optional(),
  memo: z.string().optional(),
  smsPhoneNum: z.string().optional(),
  callPassContactNum: z.string().optional(),
});

type EditFormValues = z.infer<typeof editSchema>;
type AddFormValues = z.infer<typeof addSchema>;

// --- Sub-components ---

const FormField = forwardRef<
  HTMLInputElement,
  { label: string; required?: boolean } & React.InputHTMLAttributes<HTMLInputElement>
>(({ label, required, ...props }, ref) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <input
      ref={ref}
      className="h-8 w-full rounded-md border border-gray-300 px-3 text-xs outline-none focus:border-blue-500"
      {...props}
    />
  </div>
));

// --- Detail/Edit View ---

interface DetailViewProps {
  accountGuid: string;
  companyGuid: string;
  onClose: () => void;
}

function DetailView({ accountGuid, companyGuid, onClose }: DetailViewProps) {
  const { data: detail, isLoading } = useManagerDetail(accountGuid);
  const updateMutation = useUpdateManager(companyGuid);
  const [isEditing, setIsEditing] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isPause, setIsPause] = useState(false);

  const {
    register,
    handleSubmit,
    reset: resetForm,
  } = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
  });

  useEffect(() => {
    if (detail) {
      resetForm({
        name: detail.name ?? "",
        phoneNum: detail.phoneNum ?? "",
        email: detail.email ?? "",
        department: detail.department ?? "",
        position: detail.position ?? "",
        memo: detail.memo ?? "",
        smsPhoneNum: detail.smsPhoneNum ?? "",
        callPassContactNum: detail.callPassContactNum ?? "",
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect -- detail 비동기 로드 시 상태 동기화 (의도된 동작)
      setIsActive(detail.isActive);
      setIsPause(detail.isPause);
    }
  }, [detail, resetForm]);

  const onSubmit = (data: EditFormValues) => {
    const payload = { ...data, accountGuid, isActive, isPause };
    // password 빈 문자열이면 제거 (변경 안 함)
    if (!payload.password) delete payload.password;
    updateMutation.mutate(payload, {
      onSuccess: () => {
        setIsEditing(false);
        onClose();
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!detail) return null;

  // 상세 조회 모드
  if (!isEditing) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <InfoRow label="이름" value={detail.name} />
          <InfoRow label="아이디" value={detail.accountId} />
          <InfoRow label="전화번호" value={detail.phoneNum} />
          <InfoRow label="이메일" value={detail.email} />
          <InfoRow label="부서" value={detail.department} />
          <InfoRow label="직급" value={detail.position} />
          <InfoRow label="역할" value={detail.role} />
          <InfoRow
            label="상태"
            value={detail.isActive ? "활성" : "비활성"}
            badge={detail.isActive ? "green" : "slate"}
          />
          <InfoRow
            label="일시정지"
            value={detail.isPause ? "정지됨" : "-"}
          />
          <InfoRow label="가입대기" value={detail.isWaitSignUp ? "대기중" : "-"} />
          <InfoRow label="메모" value={detail.memo} />
          <InfoRow label="SMS 번호" value={detail.smsPhoneNum} />
          <InfoRow label="콜패스 번호" value={detail.callPassContactNum} />
          <InfoRow
            label="등록일"
            value={detail.registDate ? format(new Date(detail.registDate), "yyyy-MM-dd HH:mm") : null}
          />
          <InfoRow
            label="최근 로그인"
            value={detail.lastLoginTime ? format(new Date(detail.lastLoginTime), "yyyy-MM-dd HH:mm") : null}
          />
        </div>
        <div className="flex gap-2 pt-2">
          <Button
            onClick={() => setIsEditing(true)}
            className="flex-1 bg-blue-600 text-xs font-bold hover:bg-blue-700"
          >
            수정
          </Button>
        </div>
      </div>
    );
  }

  // 수정 모드 — 기본정보 / 권한 탭
  return (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="w-full">
        <TabsTrigger value="basic" className="flex-1">
          기본정보
        </TabsTrigger>
        <TabsTrigger value="permission" className="flex-1">
          권한
        </TabsTrigger>
      </TabsList>

      <TabsContent value="basic">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <FormField label="이름" {...register("name")} />
          <FormField label="비밀번호 변경" type="password" placeholder="변경 시에만 입력" {...register("password")} />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="전화번호" {...register("phoneNum")} />
            <FormField label="이메일" {...register("email")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="부서" {...register("department")} />
            <FormField label="직급" {...register("position")} />
          </div>
          <FormField label="메모" {...register("memo")} />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="SMS 번호" {...register("smsPhoneNum")} />
            <FormField label="콜패스 번호" {...register("callPassContactNum")} />
          </div>

          <div className="space-y-2 border-t border-gray-200 pt-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700">활성 상태</label>
              <Switch checked={isActive} onCheckedChange={setIsActive} size="sm" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700">일시정지</label>
              <Switch checked={isPause} onCheckedChange={setIsPause} size="sm" />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditing(false)}
              className="flex-1 text-xs"
            >
              취소
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex-1 bg-blue-600 text-xs font-bold hover:bg-blue-700"
            >
              {updateMutation.isPending ? "저장 중..." : "저장"}
            </Button>
          </div>
        </form>
      </TabsContent>

      <TabsContent value="permission">
        <AccountPermissionTab accountGuid={accountGuid} />
      </TabsContent>
    </Tabs>
  );
}

// --- Add View ---

interface AddViewProps {
  companyGuid: string;
  onClose: () => void;
}

function AddView({ companyGuid, onClose }: AddViewProps) {
  const insertMutation = useInsertManager(companyGuid);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddFormValues>({
    resolver: zodResolver(addSchema),
  });

  const onSubmit = (data: AddFormValues) => {
    insertMutation.mutate(
      { ...data, companyGuid },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <FormField label="아이디" required {...register("id")} />
      {errors.id && <p className="text-xs text-red-500">{errors.id.message}</p>}
      <FormField label="비밀번호" required type="password" {...register("password")} />
      {errors.password && <p className="text-xs text-red-500">{errors.password.message}</p>}
      <FormField label="이름" required {...register("name")} />
      {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}

      <div className="grid grid-cols-2 gap-3">
        <FormField label="전화번호" {...register("phoneNum")} />
        <FormField label="이메일" {...register("email")} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="부서" {...register("department")} />
        <FormField label="직급" {...register("position")} />
      </div>
      <FormField label="메모" {...register("memo")} />
      <div className="grid grid-cols-2 gap-3">
        <FormField label="SMS 번호" {...register("smsPhoneNum")} />
        <FormField label="콜패스 번호" {...register("callPassContactNum")} />
      </div>

      <div className="flex gap-2 pt-2">
        <Button
          type="submit"
          disabled={insertMutation.isPending}
          className="flex-1 bg-blue-600 text-xs font-bold hover:bg-blue-700"
        >
          {insertMutation.isPending ? "등록 중..." : "등록"}
        </Button>
      </div>
    </form>
  );
}

// --- Info Row ---

function InfoRow({
  label,
  value,
  badge,
}: {
  label: string;
  value: string | null | undefined;
  badge?: "green" | "slate";
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-gray-500">{label}</span>
      {badge ? (
        <span
          className={`inline-block rounded-full px-1.5 py-0.5 text-xs ${
            badge === "green"
              ? "bg-green-100 text-green-700"
              : "bg-slate-100 text-slate-700"
          }`}
        >
          {value ?? "-"}
        </span>
      ) : (
        <span className="text-xs text-gray-900">{value || "-"}</span>
      )}
    </div>
  );
}

// --- Main Dialog ---

interface ManagerDialogProps {
  open: boolean;
  mode: "add" | "detail";
  accountGuid: string | null;
  companyGuid: string;
  onClose: () => void;
}

export function ManagerDialog({
  open,
  mode,
  accountGuid,
  companyGuid,
  onClose,
}: ManagerDialogProps) {
  const isAdd = mode === "add";
  const title = isAdd ? "담당자 추가" : "담당자 상세";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className={`max-h-[85vh] overflow-y-auto ${isAdd ? "sm:max-w-md" : "sm:max-w-2xl"}`}
      >
        <DialogHeader>
          <DialogTitle className="text-sm">{title}</DialogTitle>
          <DialogDescription className="text-xs">
            {isAdd ? "새 담당자를 등록합니다" : "담당자 정보를 조회하고 수정합니다"}
          </DialogDescription>
        </DialogHeader>

        {isAdd ? (
          <AddView key="add" companyGuid={companyGuid} onClose={onClose} />
        ) : accountGuid ? (
          <DetailView key={accountGuid} accountGuid={accountGuid} companyGuid={companyGuid} onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
